import { useAuth0 } from '@auth0/auth0-react';

import { useContext, useState, useEffect } from 'react';
import classes from '../../tables/Table.module.css'


// imports relevant for HTTP calls
import DisplayException from '../../error_messages/DisplayException';
import CircleLoader from 'react-spinners/CircleLoader';
import { SERVER_URL } from '../../config_server_url';

// ----- Imports for main window --------
import SimilarityRangeTableEditor from './SimilarityRangeTableEditor';
import PlannerSettingsValidationTable from './PlannerSettingsValidationTable';
import DisplayCandidates from './DisplayCandidates';
import DisplayCycleMeasurement from './DisplayCycleMeasurement';
import UploadManager from './UploadManager';
import InfoPopup from '../../popup_windows/InfoPopup';
import UserSessionContext from '../../user_session/context_user_session';
import CycleSetup from './CycleSetup';
import {exportCandiatesTemplateXLS, 
        exportCandiatesTableXLS,
        exportColumns2XLS} from './utils_xls_export';

function CyclePlannerEditor(props) {

    const usr_session_ctx = useContext(UserSessionContext);
    const { user } = useAuth0();

    // ------------------ Display HTTP/Server Exceptions ---------------------------------
    const [Exception, setException] = useState(false);
    const [ExceptionMessage, setExceptionMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // ------------------ Main window -----------------------------------------
    const [modified, setModified] = useState(false);
    const [cycle, setCycle] = useState({});
    const [factors, setFactors] = useState([]);
    const [subscores, setSubscores] = useState([]);
    const [excludeSimilar, setExcludeSimilar] = useState(false);
    const [cycleMeasurement, setCycleMeasurement] = useState([]);
    const [rawDataFiles, setRawDataFiles] = useState([]);
    const [experimentalProcedure, setExperimentalProcedure] = useState('')
    
    const [validationWindow, setValidationWindow] = useState(false);
    const [validationMessage, setValidationMessage] = useState([]);
    const [triggerSave, setTriggerSave] = useState(0);
    const [triggerReload, setTriggerReload] = useState(0);
    const [triggerRunPlanner, setTriggerRunPlanner] = useState(0);
    const [showModificationsDetected, setShowModificationsDetected] = useState(false)

    const [validationOnRunPlanner, setValidationOnRunPlanner] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [showResetPlanner, setShowResetPlanner] = useState(false);

    // states to manage display candidates
    const [candidates2Rows, setCandidates2Rows] = useState([]);
    const supportedStrategies = ['AI-Agent', 'Active Learning', 'Diverse', 'Random']
    const [candidateTemplate, setCandidateTemplate] = useState({factors:[], strategy:'USR_DEF', mean:null, stddev:null})
    const [modifiedCustomDesign, setModifiedCustomDesign] = useState(false);

    // manage upload data
    const [showUploadManager, setShowUploadManger] = useState(false);



    function keyWord2Strategy(keyword){
        let strategy = 'received unsupported keyword'
        if (keyword === 'AI_AGENT' ) { strategy='AI-Agent'; }
        if (keyword === 'ACTIVE_LEARNING' ) { strategy='Active Learning'; }
        if (keyword === 'DIV_SAMPL' ) { strategy='Diverse';}
        if (keyword === 'RND_SRCH' ) { strategy='Random';}  
        return strategy
    }
    function Strategy2Keyword(strategy){
        let keyword = 'received unsupported strategy'
        if (strategy === 'AI-Agent' ) { keyword='AI_AGENT'; }
        if (strategy === 'Active Learning' ) { keyword='ACTIVE_LEARNING'; }
        if (strategy === 'Diverse' ) { keyword='DIV_SAMPL';}
        if (strategy === 'Random' ) { keyword='RND_SRCH';}  
        return keyword
    }


    useEffect(() => {
        const loadData = async () => {
            setModified(false);
            setValidationWindow(false);
            setValidationMessage([]);
            setIsLoading(true); // Set loading state to true when fetch begins
            const data = {
                uuid_opt_campaign: props.uuid_opt_campaign,
                cycle_id: props.cycle_id,
            };
    
            try {
                // Prepare the fetch requests
                const request1 = fetch(`${SERVER_URL}/current_opt_campaign/load_optimization_cycle/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'uuid_tenant': usr_session_ctx.uuid_tenant
                    },
                    body: JSON.stringify(data),
                });
    
                const request2 = fetch(`${SERVER_URL}/current_opt_campaign/load_factors_active_design_space/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'uuid_tenant': usr_session_ctx.uuid_tenant
                    },
                    body: JSON.stringify(props.uuid_opt_campaign),
                });
    
                const request3 = fetch(`${SERVER_URL}/current_opt_campaign/load_subscores_active_objective/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'uuid_tenant': usr_session_ctx.uuid_tenant
                    },
                    body: JSON.stringify(props.uuid_opt_campaign),
                });
              
                const request4 = fetch(`${SERVER_URL}/current_opt_campaign/load_cycle_measurement/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'uuid_tenant': usr_session_ctx.uuid_tenant
                    },
                    body: JSON.stringify(data),
                });


                // Wait for both requests to complete
                const [response1, response2, response3, response4] = await Promise.all([
                    request1,
                    request2,
                    request3,
                    request4
                ]);
    
                // Check responses
                if (!response1.ok) {
                    throw new Error(`HTTP error ${response1.status}`);
                }
                if (!response2.ok) {
                    throw new Error(`HTTP error ${response2.status}`);
                }
                if (!response3.ok) {
                    throw new Error(`HTTP error ${response3.status}`);
                }
                if (!response4.ok) {
                    throw new Error(`HTTP error ${response4.status}`);
                }
                // Parse JSON from responses
                const loaded_cycle = await response1.json();
                const loaded_factors = await response2.json();
                const loaded_subscores = await response3.json();
                const loaded_cycle_measurement = await response4.json();

                // Update state with loaded data
                setCycle(loaded_cycle.data);
                setFactors(loaded_factors.data);
                setSubscores(loaded_subscores.data);
                let candidate_factors = loaded_factors.data.map( () => '');
                let updatedCandidateTemplate = {...candidateTemplate};
                updatedCandidateTemplate.factors = candidate_factors;
                setCandidateTemplate(updatedCandidateTemplate);
    
                // Initialize excludeSimilar
                setExcludeSimilar(loaded_cycle.data.similarity_range.length > 0);
    
                // Determine whether to show reset planner
                setShowResetPlanner(
                    loaded_cycle.data.all_candidates.length > 0 ||
                    loaded_cycle.data.uuid_ml_models.length > 0
                );
    
                // Initialize candidate table
                initialize_candidate_table(loaded_factors.data, loaded_cycle.data.selected_candidates);

                // set uploaded cycle measurement data
                setCycleMeasurement(loaded_cycle_measurement.data.data);
                setRawDataFiles(loaded_cycle_measurement.data.raw_data);
                setExperimentalProcedure(loaded_cycle_measurement.data.experimental_procedure);
                // Set loading state to false
                setIsLoading(false);
            } catch (err) {
                setIsLoading(false);
                setException(true);
                setExceptionMessage(`HTTPException: err_msg=${err.message}`);
            }
        };
        if(props.cycle_id >=0){
            loadData();
        }
    }, [props.cycle_id, props.uuid_opt_campaign, triggerReload, usr_session_ctx.uuid_tenant]);

    // If candidates exist
    function initialize_candidate_table(in_factors, in_candiates){ 
            // let col_names = [...props.factorNames];
            // col_names.unshift('');
            // setColNames(col_names);
            let factor_names= in_factors.map((factor) => { 
                        if(factor.units !== '') { 
                            return `${factor.name} [${factor.units}]`;
                        }
                        else{
                            return `${factor.name}`;
                        }
                    });
            let candidates = in_candiates.map( (candidate) => {
                    let row = { selected: false, onEdit: false };
                    factor_names.forEach((name, id) => {
                    row = Object.assign(row, { [name]: candidate.factors[id] });
                    });
                    row = Object.assign(row, { 'strategy': candidate.strategy });
                    return row;
                });
            setCandidates2Rows(candidates);
    };



function validatePlannerInput(){
        let validation_msg = [];
        if (cycle.strategy === null) {
            validation_msg.push({field: 'Strategy', validation_error: ['not selected']})
        }
        if (cycle.batch_size === '' || cycle.batch_size === null) {
            validation_msg.push({field: 'Batch Size', validation_error: ['not specified']})
        }
        if (parseInt(cycle.batch_size) < 4) {
            validation_msg.push({field: 'Batch Size', validation_error: ['Batch Size needs to be >= 4']})
        }
        // if Exclude SImilar Candidates is set
        if (excludeSimilar){
            let valid_error = [];
            factors.forEach( (factor, id) => {
                if (cycle.similarity_range[id] === '') {
                    valid_error.push(`${factor.name}: similarity range not specified`);
                }
            } );
            factors.forEach( (factor, id) => {
                if (cycle.similarity_range[id] !== '') {
                    if (cycle.similarity_range[id] >= (factor.range_max - factor.range_min) ){
                        valid_error.push(`${factor.name}: Similarity range needs to be < ${factor.range_max - factor.range_min}. 
                        Otherwise all candidates in range [${factor.range_min}, ${factor.range_max}] will be considered similar.`);
                    }
                }
            } );


            if (valid_error.length > 0) {
                validation_msg.push({field: 'Similarity Range', 
                                     validation_error: valid_error})
            }
        }
        if (validation_msg.length > 0) {
            setValidationWindow(true);
            setValidationMessage(validation_msg);
            return 'invalid';
        }
        return 'valid'
    }

    
    useEffect(() => {
        // Make sure this is not called on first render
        // Makes sure that setCycle triggered by validation are updated in the next trigger save
        if(triggerSave>0){
            const SaveAndRunPlanner = async () => {
                    await handleSave();
                    setTriggerRunPlanner(triggerRunPlanner+1);
                }
            if (validationOnRunPlanner) {
                SaveAndRunPlanner();
            }
            else{
                handleSave();
            }
        }
    }, [triggerSave])


    useEffect(() => {
        // Make sure this is not called on first render
        // Makes sure that setCycle triggered by validation are updated in the next trigger save
        if(triggerRunPlanner>0){
            handleRunPlanner();
            
        }
    }, [triggerRunPlanner])
    
    async function handleSave(){
        let status = validatePlannerInput();
        if (status === 'invalid'){
            return
        }
        setIsLoading(true);
        try{
            let cycle_data = { uuid_project: usr_session_ctx.session_info.uuid_current_project,
                               uuid_opt_campaign: props.uuid_opt_campaign, 
                               cycle_id: props.cycle_id,
                               cycle: cycle}
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/update_opt_cycle/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(cycle_data)
            });
            if(response.ok){
                const status = await response.json();
                if (status.data === 'ok'){
                    setModified(false);
                    setValidationMessage([]);
                    setValidationWindow(false);
                    setIsLoading(false);
                    return 'ok';
                }
                else {
                    throw new Error(`Backend validation error: ${status.msg}`);
                }
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
            setIsLoading(false);
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    }



    function handleDiscard() {
        setValidationMessage([]);
        setValidationWindow(false);
        setTriggerReload(triggerReload+1);
    }


    function handleSaveValidation(){
        setValidationMessage([]);
        setValidationWindow(false);
        // Makes sure that setCycle triggered by validation are updated in the next trigger save
        setTriggerSave(triggerSave+1);
    }

   function handleExitValidation(){
        setValidationMessage([]);
        setValidationWindow(false);
        setValidationOnRunPlanner(false)
    }

    // --------------- Handle Run and Reset Planner ----------------


    function onOkModifications(){
        setShowModificationsDetected(false);
    }

    function onOkNotification(){
        setShowNotification(false);
        setNotificationMessage('');
    }

    async function handleRunPlanner(){
        if (modified){
            setShowModificationsDetected(true);
            return; 
        }
        setValidationOnRunPlanner(true)
        let status = validatePlannerInput();
        if (status === 'invalid'){
            return;
        }
        setValidationOnRunPlanner(false)
        setIsLoading(true);
        try{
            let settings = { uuid_opt_campaign: props.uuid_opt_campaign, 
                             uuid_project: usr_session_ctx.session_info.uuid_current_project,
                             cycle_id: props.cycle_id}
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/run_planner/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(settings)
            });
            if(response.ok){
                const num_created_candidates = await response.json();
                if (num_created_candidates.msg !== ''){
                
                    throw new Error(`Backend validation error: ${num_created_candidates.msg}`);
                }
                else if (num_created_candidates.data < cycle.batch_size){
                    setShowNotification(true)
                    setNotificationMessage(`Only ${num_created_candidates.data} out of the requested ${cycle.batch_size} candidates meet
                                            the similarity range constraints. To increase the number of candidates, reduce the similarity range for finer sampling resolution.`)
                }
                    setTriggerReload(triggerReload+1)
                    setShowModificationsDetected(false)
                    setIsLoading(false);
                // }
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
            setIsLoading(false);
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    }


    async function handleResetPlanner(){
        setIsLoading(true);
        try{
            let settings = { uuid_project: usr_session_ctx.session_info.uuid_current_project,
                             uuid_opt_campaign: props.uuid_opt_campaign, 
                             cycle_id: props.cycle_id}
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/reset_planner/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(settings)
            });
            if(response.ok){

                const reset_planner = await response.json();
                if (reset_planner.msg !== ''){
                
                    throw new Error(`Backend validation error: ${reset_planner.msg}`);
                }
                setTriggerReload(triggerReload+1)

                // FIXME reload candidataes
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
            setIsLoading(false);
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }

    }




    // ------- Handle User Input Planner -----------------------


    function handleStrategy(event){
        if(showResetPlanner){
            setShowNotification(true)
            setNotificationMessage(`Please 'Reset Planner' before making changes to the Planner Settings.`)
        }
        else {
            let strategy = event.target.value;
            strategy = Strategy2Keyword(strategy)
            let updatedCycle = {...cycle};
            updatedCycle.strategy = strategy;
            setCycle(updatedCycle);
            setModified(true);
        }
    }


    function handleBatchSize(event){
        if(showResetPlanner){
            setShowNotification(true)
            setNotificationMessage(`Please 'Reset Planner' before making changes to the Planner Settings.`)
        }
        else {
            let batch_size = event.target.value;
            if(batch_size === '') {
                let updatedCycle = {...cycle};
                updatedCycle.batch_size = batch_size;
                setCycle(updatedCycle);
                setModified(true);
            }
            else {
                batch_size = parseInt(event.target.value);
                if (!isNaN(batch_size) && batch_size>0) {
                    let updatedCycle = {...cycle};
                    updatedCycle.batch_size = batch_size;
                    setCycle(updatedCycle);
                    setModified(true);
                }
            }
        }
    }


    function handleExcludeSimilar(event){
        if(showResetPlanner){
            setShowNotification(true)
            setNotificationMessage(`Please 'Reset Planner' before making changes to the Planner Settings.`)
        }
        else{
            if (!event.target.checked){
                setExcludeSimilar(false);
                let updatedCycle = {...cycle};
                updatedCycle.similarity_range = [];
                setCycle(updatedCycle);
                setModified(true);
            }
            if(event.target.checked){
                setExcludeSimilar(true);
                let updatedCycle = {...cycle};
                updatedCycle.similarity_range = factors.map( () => '');
                setCycle(updatedCycle);
                setModified(true);
            }
        }
    }

    function handleInputSimilarityRange(event){
        if(showResetPlanner){
            setShowNotification(true)
            setNotificationMessage(`Please 'Reset Planner' before making changes to the Planner Settings.`)
        }
        else{
            let parser = event.target.id.split('_');
            let factor_id = parseInt(parser[3]);
            let range = event.target.value;
            if(range === '') {
                let updatedCycle = {...cycle};
                updatedCycle.similarity_range[factor_id] = range;
                setCycle(updatedCycle);
                setModified(true);
            }
            else {
                range = parseFloat(event.target.value);
                if (!isNaN(range) && range>=0) {
                    let updatedCycle = {...cycle};
                    updatedCycle.similarity_range[factor_id]= range;
                    setCycle(updatedCycle);
                    setModified(true);
                }
            }
        }
    }

    // ------- Handle Actions Regarding Candidates -----------------------    
    
    async function handleDisplayPrecision(event){
        let parser = event.target.id.split('_');
        let factor_id = parseInt(parser[1]);
        let updatedCycle = {...cycle};
        if(parser[0]==='increase') {
            updatedCycle.displayed_candidate_precision[factor_id] +=1;
        }
        if(parser[0]==='reduce') {
            updatedCycle.displayed_candidate_precision[factor_id] -=1;
            if (updatedCycle.displayed_candidate_precision[factor_id]<1){
                updatedCycle.displayed_candidate_precision[factor_id] = 1;
            }
        }
        setCycle(updatedCycle);

        try{
            let cycle_data = { uuid_project: usr_session_ctx.session_info.uuid_current_project,
                               uuid_opt_campaign: props.uuid_opt_campaign, 
                               cycle_id: props.cycle_id,
                               cycle: cycle}
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant

            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/update_opt_cycle/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(cycle_data)
            });
            if(response.ok){

                await response.json();
                setCycle(updatedCycle);
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }

    }
    
    
    function handleSelectCandidate(event)
    {
        let key_id = event.target.id.split('_');
        let key = key_id[0];
        let id = key_id[1];
        // update only input, if checkbox, need to get property target.checked
        let value = event.target.checked;
        let updatedCandidates2Rows = [...candidates2Rows];
        updatedCandidates2Rows[id] = { ...updatedCandidates2Rows[id], [key]:value}; 
        setCandidates2Rows(updatedCandidates2Rows);
    }


    async function handleDeleteCandidates(event){
        // get ids of selected candiates
        const to_remove = candidates2Rows.map( (row, id) => {
                    if (row['selected'] === true ) { return id;}
                    return undefined;
                }).filter(id => id !== undefined);

        setIsLoading(true);
        try{
            let data = { uuid_project: usr_session_ctx.session_info.uuid_current_project,
                         uuid_opt_campaign: props.uuid_opt_campaign, 
                         cycle_id: props.cycle_id,
                         to_remove: to_remove}
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/remove_candidates/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(data)
            });
            if(response.ok){
                const status = await response.json();
                if (status.msg !== ''){
                        throw new Error(`Backend validation error: ${status.msg}`);
                }
                setTriggerReload(triggerReload+1)
                setIsLoading(false);
            }
            if(!response.ok){
                setIsLoading(false);
                throw new Error(`HTTP error ${response.status}`);
            }
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    }

    async function handleReplaceCandidates(event){
        // get ids of selected candiates
        const to_replace = candidates2Rows.map( (row, id) => {
                    if (row['selected'] === true ) { return id;}
                    return undefined;
                }).filter(id => id !== undefined);
        const num_to_replace = to_replace.length

        setIsLoading(true);
        try{
            let data = { uuid_project: usr_session_ctx.session_info.uuid_current_project,
                         uuid_opt_campaign: props.uuid_opt_campaign, 
                         cycle_id: props.cycle_id,
                         to_replace: to_replace}
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/replace_candidates/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(data)
            });
            if(response.ok){
                const status = await response.json();
                if (status.msg !== ''){
                        throw new Error(`Backend validation error: ${status.msg}`);
                }
                // if not all candiates could be replaced
                else if (status.data < num_to_replace){
                    setShowNotification(true)
                    setNotificationMessage(`Only ${status.data} out of the requested ${num_to_replace} candidates meet
                                            the similarity range constraints. Candiates that could not be replaced remained unaffected.`)
                }
                setTriggerReload(triggerReload+1)
                setIsLoading(false);
            }
            if(!response.ok){
                setIsLoading(false);
                throw new Error(`HTTP error ${response.status}`);
            }
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    }


    // function handleCustomDesign(event){
    //     setModifiedCustomDesign(true);
    //     let updatedCycle = {...cycle};
    //     let updatedSelectedCandidates = [...updatedCycle.selected_candidates];
    //     let updatedAllCandidates = [...updatedCycle.all_candidates];
    //     updatedAllCandidates.unshift(candidateTemplate);
    //     updatedSelectedCandidates.unshift(candidateTemplate);
    //     updatedCycle.all_candidates = updatedAllCandidates;
    //     updatedCycle.selected_candidates = updatedSelectedCandidates;
    //     setCycle(updatedCycle);
    //     let row = { selected: false, onEdit: true }
    //     let factor_names= factors.map((factor) => { 
    //         if(factor.units !== '') { 
    //             return `${factor.name} [${factor.units}]`;
    //         }
    //         else{
    //             return `${factor.name}`;
    //         }
    //     });
    //     factor_names.forEach( (name, id) => {
    //         row = Object.assign(row, { [name]: candidateTemplate.factors[id] });
    //         });
    //     row = Object.assign(row, { 'strategy': candidateTemplate.strategy });
    //     let updatedCandidates2Rows = [...candidates2Rows]
    //     updatedCandidates2Rows.unshift(row)
    //     setCandidates2Rows(updatedCandidates2Rows); 

    // }


    function handleCustomDesign(event) {
        setModifiedCustomDesign(true);
    
        const newCandidate = {
            ...candidateTemplate,
            factors: { ...candidateTemplate.factors }
        };
    
        const updatedCycle = { ...cycle };
        const updatedSelectedCandidates = [newCandidate, ...updatedCycle.selected_candidates];
        const updatedAllCandidates = [newCandidate, ...updatedCycle.all_candidates];
    
        updatedCycle.selected_candidates = updatedSelectedCandidates;
        updatedCycle.all_candidates = updatedAllCandidates;
        setCycle(updatedCycle);
    
        const row = {
            selected: false,
            onEdit: true,
            ...Object.fromEntries(factors.map((factor, id) => {
                const key = factor.units ? `${factor.name} [${factor.units}]` : factor.name;
                return [key, newCandidate.factors[id]];
            })),
            strategy: newCandidate.strategy
        };
    
        const updatedCandidates2Rows = [row, ...candidates2Rows];
        setCandidates2Rows(updatedCandidates2Rows);
    }



    function handleCustomDesignInput(event){
        let parser = event.target.id.split('_');
        let candidate_id = parseInt(parser[2]);
        let factor_id = parseInt(parser[3]);
        // console.log(candidate_id, factor_id)
        let value = event.target.value;
        let updatedCycle = {...cycle};
        let updatedSelectedCandidates = [...updatedCycle.selected_candidates];
        let updatedAllCandidates = [...updatedCycle.all_candidates];
        updatedSelectedCandidates[candidate_id].factors[factor_id] = value
        updatedAllCandidates[candidate_id].factors[factor_id] = value
        console.log(updatedAllCandidates)
        updatedCycle.all_candidates = updatedAllCandidates;
        updatedCycle.selected_candidates = updatedSelectedCandidates;
        // console.log(updatedCycle)
        setCycle(updatedCycle);
        setModifiedCustomDesign(true);
    }


    



    function handleCustomDesignSave(){
        setTriggerSave(triggerSave+1);
        setModifiedCustomDesign(false);
        initialize_candidate_table(factors, cycle.selected_candidates)
    }


    function handleCustomDesignDiscard(){
        setTriggerReload(triggerReload+1);
        setModifiedCustomDesign(false);
    }



    function handleExportTemplateXLS() {

        if(modifiedCustomDesign){
                setShowNotification(true);
                setNotificationMessage('Unsaved changes detected in custom candiadate design(s).')
                return
        }
        const filename = `candidates_upload_template_cycle_${props.cycle_id+1}.xlsx`;
        exportCandiatesTemplateXLS(factors, subscores, cycle, filename);
    }


    function handleExportTableXLS() {
        if(modifiedCustomDesign){
            setShowNotification(true);
            setNotificationMessage('Unsaved changes detected in custom candiadate design(s).')
            return
        }
        const filename = `candidates_cycle_${props.cycle_id+1}.xlsx`;
        exportCandiatesTableXLS(factors, cycle, filename);
    }

    function handleExportCycleMeasurementTableXLS(){
        const filename = `cycle_measurement_${props.cycle_id+1}.xlsx`;
        exportColumns2XLS(cycleMeasurement[0].csv_data.columns, filename);
    }


    // ---------------------- Handel Functions regarding CycleMeasurments --------------------------

    async function handleDisplayPrecisionCycleMeasurement(event){
        let parser = event.target.id.split('_');
        let col_id = parseInt(parser[1]);
        let updatedCycle = {...cycle};
        if(parser[0]==='increase') {
            updatedCycle.displayed_cycle_measurement_precision[col_id] +=1;
        }
        if(parser[0]==='reduce') {
            updatedCycle.displayed_cycle_measurement_precision[col_id] -=1;
            if (updatedCycle.displayed_cycle_measurement_precision[col_id]<1){
                updatedCycle.displayed_cycle_measurement_precision[col_id] = 1;
            }
        }
        setCycle(updatedCycle);

        try{
            let cycle_data = { uuid_project: usr_session_ctx.session_info.uuid_current_project,
                               uuid_opt_campaign: props.uuid_opt_campaign, 
                               cycle_id: props.cycle_id,
                               cycle: cycle}
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/update_opt_cycle/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(cycle_data)
            });
            if(response.ok){
                await response.json();
                setCycle(updatedCycle);
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }

    }



    // ---------------- Handle Upload of the Results of the Measurement
    function handleUploadResults(){
        setShowUploadManger(true);

    }


    async function handleRemoveUpload(){
        setIsLoading(true);
        try{
            let data = { uuid_project: usr_session_ctx.session_info.uuid_current_project,
                         uuid_opt_campaign: props.uuid_opt_campaign, 
                         cycle_id: props.cycle_id }
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': props.user,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/remove_upload_cyclemeasurement/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(data)
            });
            if(response.ok){
                await response.json();
                setTriggerReload(triggerReload+1);
                setIsLoading(true);
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }

    }


    function exitUploadManager(event){
        setShowUploadManger(false);
        if(event==='on_upload'){
            setTriggerReload(triggerReload+1)
        }
    }


    async function handleDownloadRawDataFile(event){
        let parser = event.target.id.split('_');
        let raw_data_id = parseInt(parser[1]);
        try{
            
            let headers = {
                'Content-Type': 'application/json',
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/download_raw_data_file/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(rawDataFiles[raw_data_id].uuid_raw_data_file)
            });
            if(response.ok){
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', rawDataFiles[raw_data_id].filename); // Change this if you want a different name for the downloaded file
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
        } catch(err) {
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    }


    let show_uploaded_candiates = (<></>)
    if (cycleMeasurement.length>0){
        show_uploaded_candiates = (<>
                <DisplayCycleMeasurement 
                                    displayPrecision={cycle.displayed_cycle_measurement_precision}
                                    width={props.width}
                                    cycleMeasurement={cycleMeasurement}
                                    rawDataFiles={rawDataFiles}
                                    experimentalProcedure={experimentalProcedure}
                                    handleDisplayPrecision={handleDisplayPrecisionCycleMeasurement}
                                    downloadFile={handleDownloadRawDataFile}
                                    handleExportTableXLS={handleExportCycleMeasurementTableXLS} />
        </>);
    }   


    let active =(<></>);

    if(cycle.active){
        active = (<>
                <div style={{display:'flex', alignItems:'center'}}>
                    <div style={{marginTop:'0px', marginBottom:'0px', marginRight:'8px', height:'10px', width:'10px', borderRadius:'50%', background:'var(--aixelo-green)'}}></div>
                    <div style={{marginTop:'0px', marginBottom:'0px', marginRight:'0px'}}> active cycle</div>
                </div>
        </>);
    }
    if(!cycle.active){
        active = (<>
                <div style={{display:'flex', alignItems:'center'}}>
                    <div style={{marginTop:'0px', marginBottom:'0px', marginRight:'8px', height:'10px', width:'10px', borderRadius:'50%', background:'var(--aixelo-red)'}}></div>
                    <div style={{marginTop:'0px', marginBottom:'0px', marginRight:'0px'}}> cycle inactive</div>
                </div>
        </>);
    }

    let similarity_range = (<></>);
    if(excludeSimilar){
        similarity_range = (<>
            <div style = {{fontSize:'14px', alignItems:'top', margin:'0px', marginLeft:'10px', marginBottom:'5px', height:'fit-content', width:'95%', overflow:'auto'}}>
                <div style={{fontWeight:'bold', marginTop:'10px', marginBottom:'10px',}}>Similarity ranges:</div>
                <SimilarityRangeTableEditor 
                        similarity_range={cycle.similarity_range}
                        factors={factors}
                        inputHandler={handleInputSimilarityRange}
                        inputHandlerSlider={handleInputSimilarityRange}/>


                <div style={{display: 'flex', marginTop:'10px', flexDirection: 'row', alignItems:'center'}}>
                    <div style={{width: '300px', minWidth: '300px', marginRight:'15px'}}>Two candidates are considered similar if the difference in all factors is smaller than the 
                        <span style={{fontWeight:'bold', color:'black'}}> similarity range.</span>
                    </div>
                    <div style={{display:'flex', width:'40px', minWidth:'60px', minHeight:'40px', height: '40px', background: 'var(--aixelo-gray-light)', alignItems:'center', justifyContent:'center'}}>
                        <div style={{width:'32px', height: '27px', background: 'black'}}>
                            <div style={{marginLeft:'7px', marginTop:'7px', width:'6px', height: '6px', borderRadius:'50%', background: 'white'}}> </div>
                            <div style={{marginLeft:'16px', marginTop:'3px', width:'6px', height: '6px', borderRadius:'50%', background: 'white'}}> </div>
                        </div>
                    </div>                         
                </div>
            </div>
        </>);
    }  

    let action_panel = (<></>);
    if (modified === true) {
        action_panel = (<>
            <div style={{display:'flex', marginTop:'0px'}}>
                <button className={'tableButton'} id={`save_planner_${props.cycle_id}`} style={{marginTop:'0px', marginBottom:'0px'}} onClick={handleSave}>
                    save
                </button>
                <button className={'tableButton'} id={`discared_planner_${props.cycle_id}`} style={{marginTop:'0px', marginBottom:'0px'}} onClick={handleDiscard}>
                    discard
                </button>
            </div>
            </>);
    }

    let planner = (<></>);
    let planner_action_panel = (<></>);
    let display_candidates = (<></>);
    let upload_action_panel = (<></>);
    let endofcycle_action_panel = (<></>);


    if (!showResetPlanner){
        planner_action_panel = (<>
            <button className={'actionButton'} 
                    style={{marginLeft:'20px', marginTop:'20px', marginBottom:'0px', width:'140px'}}
                    onClick={handleRunPlanner}>
                Run Planner
            </button>   
        </>);
    }

    if (showResetPlanner){
        planner_action_panel =(<>
            <button className={'actionButton'} 
                    style={{marginLeft:'20px', marginTop:'20px', marginBottom:'0px', width:'140px'}}
                    onClick={handleResetPlanner}>
                Reset Planner
            </button>
        </>);   
        if(cycle.uuid_uploaded_cycle_measurement_training_data.length===0){
            upload_action_panel =(<>
                <button className={'actionButton'} 
                        style={{marginLeft:'20px', marginTop:'20px', marginBottom:'0px', width:'140px'}}
                        onClick={handleUploadResults}>
                    Upload Results
                </button>  
            </>);
        }
        else{
            upload_action_panel =(<>
                <button className={'actionButton'} 
                        style={{marginLeft:'20px', marginTop:'20px', marginBottom:'0px', width:'140px'}}
                        onClick={handleRemoveUpload}>
                    Remove Upload
                </button>  
            </>);
            endofcycle_action_panel =(<>
                <button className={'actionButton'} 
                    style={{marginLeft:'20px', marginTop:'20px', marginBottom:'0px', width:'140px'}}
                    onClick={props.handleCreateCycle}>
                    Start Next Cycle
                </button>  
            </>);



        }



    }

    if(!validationWindow && cycle.uuid_training_data){
        planner = (<>
                <div style={{display:'flex', alignItems:'center', width:'95%'}}>
                    <h4 style = {{margin:'0px', marginTop:'10px', marginLeft:'10px', marginBottom:'5px', width:'fit-content'}} >
                        Planner Settings 
                    </h4>
                    <div style={{flexGrow: '1'}}></div>
                    {active}
                </div>
                <div style = {{display:'flex', fontSize:'14px', alignItems:'center', margin:'0px', marginLeft:'10px', marginBottom:'5px', height:'fit-content', width:'95%'}}>
                    <div style={{width:'80px'}}>Strategy:</div>
                    <select name='strategy'
                            className={classes.selectDefault}
                            id={`strategy_${props.cycle_id}`} 
                            onChange={handleStrategy}
                            value={cycle.strategy !== null ? keyWord2Strategy(cycle.strategy) : ''} >
                            <option value='' disabled selected>{'select strategy'}</option>
                                {supportedStrategies.map((option, index) => {
                                    if(option === 'AI-Agent' && cycle.uuid_training_data.length === 0){
                                        return (<option key={index} value={option} disabled>
                                            {option}
                                        </option>)
                                    }
                                    else if(option === 'Active Learning' && cycle.uuid_training_data.length === 0){
                                        return (<option key={index} value={option} disabled>
                                            {option}
                                        </option>)
                                    }
                                    else{
                                        return (<option key={index} value={option}>
                                                    {option}
                                                </option>)
                                    }
                                })}
                    </select>
                </div>
                <div style = {{display:'flex', fontSize:'14px', alignItems:'center', margin:'0px', marginLeft:'10px', marginBottom:'5px', height:'fit-content', width:'95%'}}>
                    <div style={{width:'80px'}}>Batch Size:</div>
                    <input 
                        id={`batch_size_${props.cycle_id}`}
                        type='text'  
                        pattern='[0-9]+'
                        placeholder='enter' 
                        autoComplete='false' 
                        value={cycle.batch_size !== null ? cycle.batch_size : ''} 
                        onChange={handleBatchSize}
                        style={{fontSize: '14px', margin:'0px', border:'0.5px solid var(--aixelo-gray)', width:'60px'}}/>

                </div>
                <div style = {{display:'flex', marginTop:'15px', fontSize:'14px', alignItems:'center', margin:'0px', marginLeft:'10px', marginBottom:'5px', height:'fit-content', width:'95%'}}>
                    <div style={{width:'20px', marginRight:'3px', display:'flex'}}>
                        <input 
                                id={`is_similarity_${props.cycle_id}`} 
                                style={{margin:'0px', height:'14px'}}
                                onChange={handleExcludeSimilar} 
                                type='checkbox' 
                                checked={excludeSimilar}
                            /> 
                    </div>
                    <div style={{marginTop:'0px'}}>Exclude Similar Candidates</div>
                </div>
                {similarity_range}
                {/* <div style = {{marginLeft:'10px', marginBottom:'10px', marginTop:'15px'}} >
                    <button className={'actionButtonSmall'} style={{width:'120px', margin:'0px'}} onClick={props.handleDeleteCycle} >Import Data</button> 
                </div> */}
            </>);

        if(showResetPlanner){
            display_candidates = (<>
                <DisplayCandidates 
                    width={props.width}
                    candidates={candidates2Rows}
                    factorNames={factors.map((factor) => { 
                                        if(factor.units !== '') { 
                                            return `${factor.name} [${factor.units}]`;
                                        }
                                        else{
                                            return `${factor.name}`;
                                        }
                                    })}
                    displayPrecision={cycle.displayed_candidate_precision}
                    modifiedCustomDesign={modifiedCustomDesign}
                    handleDisplayPrecision={handleDisplayPrecision}
                    onSelectCandidates={handleSelectCandidate}
                    handleCustomDesignInput={handleCustomDesignInput}
                    handleCustomDesign={handleCustomDesign}
                    handleCustomDesignSave={handleCustomDesignSave}
                    handleCustomDesignDiscard={handleCustomDesignDiscard}
                    handleDelete={handleDeleteCandidates}
                    handleReplace={handleReplaceCandidates}
                    handleExportTemplateXLS={handleExportTemplateXLS}
                    handleExportTableXLS={handleExportTableXLS}
                   />        
            </>);
        }


    }

    if(showUploadManager){


        return (<> 
                    <UploadManager 
                            uuid_opt_campaign={props.uuid_opt_campaign}
                            cycle_id = {props.cycle_id}
                            height={props.height}
                            width={props.width}
                            factors={factors}
                            subscores={subscores}
                            handleExit={exitUploadManager}
                            returnTemplateXLS={handleExportTemplateXLS}
                    />
                </>);


    }



    if(validationWindow) {
        let action_panel = (
                            <>
                                <div style={{marginTop:'25px'}}>
                                    <div style={{marginLeft:'20px', marginBottom:'5px', fontSize:'14px'}}>Feel free to click 'Exit' to fix inconsistencies directly in Planner Settings Editor.</div>
                                    <div style={{display:'flex', margin:'0px'}}>
                                        <button className={'actionButtonSmall'} style={{marginLeft: '20px', right: '0px', marginBottom:'10px'}} onClick={handleSaveValidation}>Save</button>
                                        <button className={'actionButtonSmall'} style={{marginLeft: '0px', right: '0px', marginBottom:'10px'}} onClick={handleExitValidation}>Exit</button>
                                    </div>
                                </div>
                            </>
                            );


        let AIAgentEnabled = true;
        if (cycle.uuid_training_data.length === 0){
            AIAgentEnabled = false;
        }

        let show_validations = (<>
                                    <div style={{position: 'relative', marginLeft:'20px', marginTop:'25px', height:'fit-content', width:`${props.width * 0.90 - 60}px`, overflow:'auto'}}>
                                        <PlannerSettingsValidationTable 
                                                            validations={validationMessage} 
                                                            supportedStrategies={supportedStrategies}
                                                            AIAgentEnabled={AIAgentEnabled}
                                                            similarity_range={cycle.similarity_range}
                                                            factors={factors}
                                                            handleInputSimilarityRange = {handleInputSimilarityRange}
                                                            handleStrategy={handleStrategy}
                                                            handleBatchSize={handleBatchSize}/>
                                    </div>
                                </>);
        planner = (<>
                    <div className = {'builder_canvas'} style={{height:props.height}} >
                        <h3 style={{marginTop:'10px', marginLeft:'20px', marginBottom:'10px', padding:'0px', color:'var(--aixelo-red)'}}>Planner Settings Validation</h3>
                        <div style={{marginLeft:'20px', marginBottom:'0px', fontSize:'14px'}}>The following inconsistencies in the provided planner setting have been detected.</div>
                        {show_validations}
                        {action_panel}
                    </div>
                </>);
        return (<>{planner}</>)
    }

    if(isLoading && !Exception){
        return (<div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'90%'}} >
                    <CircleLoader loading={isLoading} size={40} color='var(--aixelo-green)' />
            </div>);
    }

    if(Exception){
        return (<>
                <div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'90%'}} >
                    <DisplayException>
                        {ExceptionMessage}
                    </DisplayException>  
                </div>
            </>);
    }

    if(showModificationsDetected){
        return (<>
                    <div className = {'builder_canvas'} >
                        <InfoPopup ypos={'60px'} handleOk={onOkModifications} >
                            Unsaved changes detected in Planner Settings.
                        </InfoPopup>  
                </div>
                </>);
    }

    if(showNotification){
        return (<>
            <div className = {'builder_canvas'} >
                <InfoPopup ypos={'60px'} handleOk={onOkNotification} >
                    {notificationMessage}
                </InfoPopup>  
        </div>
        </>);
    }
            
    return (<>
        <CycleSetup width={props.width} 
                    cycle_id={props.cycle_id}
                    uuid_opt_campaign={props.uuid_opt_campaign}
                    onView={props.onView}
                    onEdit={props.onEdit}/> 
            <div style = {{width: `${props.width * 0.91 - 20}px`, marginLeft: '20px', background: 'white', marginTop: '20px', marginBottom: '0px', border:'0.5px solid var(--aixelo-gray-light)'}}>
                {planner}
            </div>
            <div style={{marginLeft:'20px', marginTop:'10px', marginBottom:'0px'}}>
                {action_panel}
            </div>
            {planner_action_panel}
            {display_candidates}
            {upload_action_panel}
            {show_uploaded_candiates}
            {endofcycle_action_panel}
        </>);

}


export default CyclePlannerEditor